import { ChakraV3 } from '@piccolohealth/ui';
import { TextSelection } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import type { TableOfContentDataItem } from '@tiptap-pro/extension-table-of-content';
import React from 'react';
import { scrollIntoView } from '../../utils/scrollIntoView';

interface TableOfContentItemProps {
  item: TableOfContentDataItem;
  onItemClick: (e: React.MouseEvent<HTMLAnchorElement>, id: string) => void;
}

export const TableOfContentItem = (props: TableOfContentItemProps) => {
  const { item, onItemClick } = props;

  return (
    <ChakraV3.Box pl={2 * (item.level - 1)}>
      <ChakraV3.Link
        px={2}
        py={0.5}
        rounded='md'
        fontSize='sm'
        fontWeight='normal'
        display='block'
        href={`#${item.id}`}
        onClick={(e) => onItemClick(e, item.id)}
        overflow='hidden'
        textOverflow='ellipsis'
        whiteSpace='nowrap'
      >
        {item.itemIndex}. {item.textContent}
      </ChakraV3.Link>
    </ChakraV3.Box>
  );
};

export const TableOfContentEmptyState = () => {
  return (
    <ChakraV3.Text color='secondary' px={3} fontSize='sm'>
      Start editing your document to see the outline.
    </ChakraV3.Text>
  );
};

interface Props {
  editor: Editor | null;
}

export const TableOfContentView = (props: Props) => {
  const { editor } = props;

  const items: TableOfContentDataItem[] = editor?.storage?.tableOfContent?.content ?? [];

  if (items.length === 0 || editor?.isEmpty) {
    return <TableOfContentEmptyState />;
  }

  const onItemClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();

    if (editor) {
      const element = editor.view.dom.querySelector(`[data-toc-id="${id}"`);

      if (!element) {
        return;
      }

      const pos = editor.view.posAtDOM(element, 0);

      // set focus
      const tr = editor.view.state.tr;

      tr.setSelection(new TextSelection(tr.doc.resolve(pos)));

      editor.view.dispatch(tr);

      editor.view.focus();

      scrollIntoView(element);
    }
  };

  return (
    <ChakraV3.Stack gap={1} px={2}>
      {items.map((item) => (
        <TableOfContentItem key={item.id} item={item} onItemClick={onItemClick} />
      ))}
    </ChakraV3.Stack>
  );
};
